import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import type { BindItem } from './types'

interface VirtualListProps {
  items: BindItem[]
  itemHeight: number
  containerHeight: number
  renderItem: (item: BindItem, index: number) => React.ReactNode
  overscan?: number
  className?: string
  style?: React.CSSProperties
}

const VirtualList: React.FC<VirtualListProps> = ({
  items,
  itemHeight,
  containerHeight,
  renderItem,
  overscan = 5,
  className = '',
  style = {},
}) => {
  const [scrollTop, setScrollTop] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  // 计算可见范围
  const visibleRange = useMemo(() => {
    const start = Math.floor(scrollTop / itemHeight)
    const end = Math.min(
      start + Math.ceil(containerHeight / itemHeight) + overscan,
      items.length
    )
    const visibleStart = Math.max(0, start - overscan)
    
    return {
      start: visibleStart,
      end,
      offsetY: visibleStart * itemHeight,
    }
  }, [scrollTop, itemHeight, containerHeight, overscan, items.length])

  // 处理滚动事件
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop)
  }, [])

  // 滚动到指定索引
  const scrollToIndex = useCallback((index: number) => {
    if (containerRef.current) {
      const scrollTop = index * itemHeight
      containerRef.current.scrollTop = scrollTop
    }
  }, [itemHeight])

  // 滚动到顶部
  const scrollToTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
    }
  }, [])

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      const scrollTop = (items.length - 1) * itemHeight
      containerRef.current.scrollTop = scrollTop
    }
  }, [items.length, itemHeight])

  // 暴露滚动方法给父组件
  useEffect(() => {
    if (containerRef.current) {
      (containerRef.current as any).scrollToIndex = scrollToIndex
      ;(containerRef.current as any).scrollToTop = scrollToTop
      ;(containerRef.current as any).scrollToBottom = scrollToBottom
    }
  }, [scrollToIndex, scrollToTop, scrollToBottom])

  // 计算总高度
  const totalHeight = items.length * itemHeight

  // 渲染可见项目
  const visibleItems = useMemo(() => {
    return items
      .slice(visibleRange.start, visibleRange.end)
      .map((item, index) => {
        const actualIndex = visibleRange.start + index
        return (
          <div
            key={`${item.id || actualIndex}-${actualIndex}`}
            style={{
              position: 'absolute',
              top: actualIndex * itemHeight,
              left: 0,
              right: 0,
              height: itemHeight,
            }}
          >
            {renderItem(item, actualIndex)}
          </div>
        )
      })
  }, [items, visibleRange, itemHeight, renderItem])

  return (
    <div
      ref={containerRef}
      className={`virtual-list ${className}`}
      style={{
        height: containerHeight,
        overflow: 'auto',
        position: 'relative',
        ...style,
      }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems}
      </div>
    </div>
  )
}

export default VirtualList
